import { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState, SkillStandardResponse } from "@/models";
import { MasteryPacing, SkillType } from "@/enums";
import moment from "moment";
import { getSkillsMetadata } from "@/services/weeklyPlanner/query/getSkillsMetadata";
import { getStudentSkillsMastery } from "@/services/weeklyPlanner/query/getStudentSkillsMastery";
import { getTeacherAssignments } from "@/services/weeklyPlanner/query/getTeacherAssignments";
import { getCurriculum } from "@/services/weeklyPlanner/query/getCurriculum";
import { aggregateAssignmentsToLessons } from "@/utilities/aggregateAssignmentsToLessons";
import { GradeNumericToString } from "@/enums";
import { getStudentSkillStandards } from "@/services/weeklyPlanner/query/getStudentSkillStandards";

export function usePreTeachData() {
  const roster = useSelector((state: RootState) => state.reports?.roster);
  const studentsRoster = roster?.data?.student || [];
  const classroomId = roster?.ui?.selections?.classroom?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [prerequisiteSkills, setPrerequisiteSkills] = useState<any[]>([]);
  const [redKids, setRedKids] = useState<any[]>([]);
  const [allStudentsMasteryData, setAllStudentsMasteryData] = useState<Map<string, any>>(new Map());
  const [nextWeekCurriculumData, setNextWeekCurriculumData] =
    useState<any>(null);

  const hasFetchedCurriculum = useRef(false);
  const hasFetchedPrerequisites = useRef(false);
  const hasFetchedMastery = useRef(false);

  const students = useMemo(() => {
    return studentsRoster
      .filter((s: any) => s.classroom_id.includes(`${classroomId}`))
      .map((s: any) => ({
        ...s,
        firstName: s.first_name,
        lastName: s.last_name,
      }));
  }, [studentsRoster, classroomId]);

  const { nextWeekStart, nextWeekEnd } = useMemo(
    () => ({
      nextWeekStart: moment().add(1, "week").startOf("week"),
      nextWeekEnd: moment().add(1, "week").startOf("week").add(5, "days"),
    }),
    []
  );

  const nextWeekFocusSkills = useMemo(() => {
    const skills: any[] = [];
    if (nextWeekCurriculumData?.tracks) {
      nextWeekCurriculumData.tracks.forEach((track: any) => {
        track.units?.forEach((unit: any) => {
          unit.lessons?.forEach((lesson: any) => {
            const lessonDate = moment(lesson.dateFrom);
            if (lessonDate.isBetween(nextWeekStart, nextWeekEnd, "day", "[]")) {
              lesson.skills
                .filter((skill: any) => skill.skillType === SkillType.FOCUS)
                .forEach((skill: any) => {
                  skills.push({
                    ...skill,
                    lessonName: lesson.lessonName || lesson.name,
                    lessonDate: lesson.dateFrom,
                    unitName: unit.name,
                    trackName: track.name,
                  });
                });
            }
          });
        });
      });
    }
    return skills;
  }, [nextWeekCurriculumData, nextWeekStart, nextWeekEnd]);

  useEffect(() => {
    hasFetchedCurriculum.current = false;
    hasFetchedPrerequisites.current = false;
    hasFetchedMastery.current = false;
    setNextWeekCurriculumData(null);
    setPrerequisiteSkills([]);
    setRedKids([]);
    setAllStudentsMasteryData(new Map());
  }, [classroomId]);

  useEffect(() => {
    if (!classroomId || hasFetchedCurriculum.current) return;

    const fetchNextWeekCurriculum = async () => {
      setIsLoading(true);
      try {
        const assignments = await getTeacherAssignments(
          classroomId,
          nextWeekStart.format("YYYY-MM-DD"),
          nextWeekEnd.format("YYYY-MM-DD"),
          GradeNumericToString[studentsRoster[0]?.grade]
        );

        const lessonPlanIds = [
          ...new Set(
            assignments?.assignment
              ?.map((a: any) => a.lessonPlanId)
              .filter(Boolean)
          ),
        ];

        if (lessonPlanIds.length > 0) {
          const curriculumResponse = await getCurriculum(lessonPlanIds);
          if (curriculumResponse?.getCurriculumInfoForLessons) {
            const aggregatedCurriculum = aggregateAssignmentsToLessons({
              assignments: assignments.assignment,
              curriculumInfo: curriculumResponse.getCurriculumInfoForLessons,
              dateFrom: nextWeekStart.format("YYYY-MM-DD"),
              dateTo: nextWeekEnd.format("YYYY-MM-DD"),
              stories: {},
              studentCount: students.length,
            });

            setNextWeekCurriculumData(aggregatedCurriculum);
          }
        }
      } catch (error) {
        console.error("Error fetching next week curriculum:", error);
      } finally {
        hasFetchedCurriculum.current = true;
        setIsLoading(false);
      }
    };

    fetchNextWeekCurriculum();
  }, [
    classroomId,
    studentsRoster,
    nextWeekStart,
    nextWeekEnd,
    students.length,
  ]);

  useEffect(() => {
    if (!nextWeekFocusSkills.length || hasFetchedPrerequisites.current) return;

    const fetchPrerequisites = async () => {
      setIsLoading(true);
      try {
        const skillIds = [
          ...new Set(nextWeekFocusSkills.map((s) => s.skillId).filter(Boolean)),
        ];
        const { skillsMetadata } = await getSkillsMetadata(skillIds);
        const prereqIds = [
          ...new Set(skillsMetadata.flatMap((m) => m.preReqSkills)),
        ];
        if (!prereqIds.length) return;

        const { skillsMetadata: prereqMetadata } =
          await getSkillsMetadata(prereqIds);

        const prereqSkillNames = new Map();
        if (students.length > 0) {
          const firstStudentId = students[0].id;

          const skillNamePromises = prereqIds.map(async (skillId) => {
            try {
              const {
                getStudentSkillStandards: skillStandardsResponse,
              }: { getStudentSkillStandards: SkillStandardResponse[] } =
                await getStudentSkillStandards({
                  studentId: firstStudentId,
                  skillId,
                });
              return {
                skillId,
                skillName:
                  skillStandardsResponse?.[0]?.skill?.skillName || skillId,
              };
            } catch (error) {
              console.error(`Error fetching skill name for ${skillId}:`, error);
              return { skillId, skillName: skillId };
            }
          });

          const skillNameResults = await Promise.all(skillNamePromises);
          skillNameResults.forEach(({ skillId, skillName }) => {
            prereqSkillNames.set(skillId, skillName);
          });
        }

        const enhancedSkills = nextWeekFocusSkills.map((skill) => {
          const metadata = skillsMetadata.find(
            (m) => m.skillId === skill.skillId
          );
          const prerequisites =
            metadata?.preReqSkills.map((prereqId) => {
              const prereqMeta = prereqMetadata.find(
                (p) => p.skillId === prereqId
              );
              return {
                skillId: prereqId,
                timeToMaster: prereqMeta?.timeToMaster,
                displayName: prereqSkillNames.get(prereqId) || prereqId,
              };
            }) || [];

          return {
            ...skill,
            prerequisites: prerequisites,
            timeToMaster: metadata?.timeToMaster,
          };
        });

        const skillsWithPrerequisites = enhancedSkills.filter(
          (skill) => skill.prerequisites && skill.prerequisites.length > 0
        );
        setPrerequisiteSkills(skillsWithPrerequisites);
      } catch (error) {
        console.error("Error fetching prerequisites:", error);
      } finally {
        hasFetchedPrerequisites.current = true;
        setIsLoading(false);
      }
    };

    fetchPrerequisites();
  }, [nextWeekFocusSkills, students]);

  useEffect(() => {
    if (
      prerequisiteSkills.length === 0 ||
      students.length === 0 ||
      hasFetchedMastery.current
    )
      return;

    const fetchMasteryData = async () => {
      setIsLoading(true);
      try {
        const prereqSkillIds = [
          ...new Set([
            ...prerequisiteSkills.flatMap(
              (skill) =>
                skill.prerequisites?.map((prereq: any) => prereq.skillId) || []
            ),
          ]),
        ];

        if (prereqSkillIds.length === 0) {
          setRedKids([]);
          setAllStudentsMasteryData(new Map());
          return;
        }

        const { studentSkillsMastery } = await getStudentSkillsMastery({
          studentId: students.map((s: any) => s.id),
          skillId: prereqSkillIds,
          dateTo: moment().format("YYYY-MM-DD"),
          dateFrom: moment().format("YYYY-MM-DD"),
        });

        if (studentSkillsMastery.length === 0) {
          setRedKids([]);
          setAllStudentsMasteryData(new Map());
          return;
        }

        const masteryDataMap = new Map();
        studentSkillsMastery.forEach((studentMastery: any) => {
          const studentSkillMap = new Map();
          studentMastery.records.forEach((record: any) => {
            studentSkillMap.set(record.skillId, {
              pacing: record.pacing,
              exposureCount: record.exposureCount || 0,
              errorCount: record.errorCount || 0,
              skillId: record.skillId
            });
          });
          masteryDataMap.set(studentMastery.studentId, studentSkillMap);
        });
        setAllStudentsMasteryData(masteryDataMap);

        const studentSkillMap = new Map();

        prerequisiteSkills.forEach((focusSkill) => {
          students.forEach((student: any) => {
            const studentMastery = studentSkillsMastery.find(
              (m: any) => m.studentId === student.id
            );

            if (!studentMastery) return;

            // Find the first prerequisite where student is red
            const redPrereq = (focusSkill.prerequisites || []).find(
              (prereq: any) => {
                const prereqRecord = studentMastery.records.find(
                  (r: any) => r.skillId === prereq.skillId
                );
                return prereqRecord?.pacing === "BELOW_GRADE_LEVEL";
              }
            );

            if (redPrereq) {
              const prereqRecord = studentMastery.records.find(
                (r: any) => r.skillId === redPrereq.skillId
              );

              const key = student.id;
              studentSkillMap.set(key, {
                student,
                focusSkill,
                underdevelopedPrerequisite: redPrereq,
                exposureCount: prereqRecord?.exposureCount || 0,
                errorCount: prereqRecord?.errorCount || 0,
                pacing: prereqRecord?.pacing,
              });
            }
          });
        });

        const unique = Array.from(studentSkillMap.values());

        setRedKids(unique);
      } catch (error) {
        console.error("Error fetching mastery data:", error);
        setRedKids([]);
        setAllStudentsMasteryData(new Map());
      } finally {
        hasFetchedMastery.current = true;
        setIsLoading(false);
      }
    };

    fetchMasteryData();
  }, [prerequisiteSkills, students]);

  const getStudentMasteryForSkill = (studentId: string, skillId: string) => {
    const studentData = allStudentsMasteryData.get(studentId);
    return studentData?.get(skillId) || {
      pacing: MasteryPacing.NO_DATA,
      exposureCount: 0,
      errorCount: 0,
      skillId
    };
  };

  return {
    isLoading,
    nextWeekFocusSkills,
    prerequisiteSkills,
    redKids,
    students,
    nextWeekStart,
    nextWeekEnd,
    allStudentsMasteryData,
    getStudentMasteryForSkill,
  };
}

export default usePreTeachData;
